// Economic Calendar API Service
// FairEconomy.media multi-format feed - Free, no API key required
// Data sourced from Forex Factory calendar
//
// RATE LIMIT: As of March 2025, each format (JSON/CSV/XML) has its own
// independent rate limit of 2 requests per 5 minutes.
// The Electron main process cascades through formats automatically.

import { parseEtToUtc, TIMEZONE_OPTIONS as TS_TIMEZONE_OPTIONS } from '../../services/timeService';

const CACHE_KEY = 'economicCalendar_apiCache';
const CACHE_DURATION = 48 * 60 * 60 * 1000; // 48 hours
// Floor between auto-refreshes. Economic calendar data is weekly — events
// don't change minute-to-minute, and the 48hr cache covers most repeat hits.
// 4hr matches the cache strategy and stays well under FairEconomy's per-format
// rate limit (2 req / 5 min). User-initiated refresh can bypass this guard.
const MIN_FETCH_INTERVAL = 4 * 60 * 60 * 1000; // 4 hours

// In-flight request deduplication
let _inflightPromise = null;
let _lastFetchAttempt = 0;

// parseEtToUtc moved to src/services/timeService.js — imported above.

// Timezone display options for Settings dropdown — sourced from timeService
// so the Settings dropdown and the rest of the app stay in lockstep.
export const TIMEZONE_OPTIONS = TS_TIMEZONE_OPTIONS;

/**
 * Parse the FairEconomy XML feed into an array of event objects
 * XML structure: <weeklyevents><event><title/><country/><date/><time/><impact/><forecast/><previous/></event>...</weeklyevents>
 *
 * Times in the XML are Eastern Time. We parse them as ET and convert to local.
 *
 * @param {string} xmlText - Raw XML string from the API
 * @returns {Array} Array of parsed event objects
 */
const parseEventsXml = (xmlText) => {
  const events = [];

  // Guard: detect HTML rate-limit page instead of XML
  if (!xmlText || typeof xmlText !== 'string') {
    console.warn('Economic calendar: empty or invalid response');
    return events;
  }
  if (xmlText.includes('<!DOCTYPE html') || xmlText.includes('Request Denied') || xmlText.includes('<html')) {
    console.warn('Economic calendar: received HTML instead of XML (likely rate-limited)');
    return events;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'text/xml');

    // Check for XML parse error
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      console.warn('Economic calendar: XML parse error:', parseError.textContent?.substring(0, 200));
      return events;
    }

    // Get all child element nodes (each is an <event>)
    const nodes = doc.documentElement.childNodes;

    let id = 0;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (node.nodeType !== 1) continue; // skip text nodes

      const timeText = node.querySelector('time')?.textContent?.trim();
      if (!timeText) continue; // skip tentative events with no time

      const dateText = node.querySelector('date')?.textContent?.trim();
      if (!dateText) continue;

      // FairEconomy times are Eastern Time (ET), not UTC.
      // Date format: "MM-DD-YYYY", Time format: "h:mmam/pm" (e.g. "8:30am").
      // parseEtToUtc handles DST-aware ET → UTC conversion (see helper above).
      const dtUtc = parseEtToUtc(dateText, timeText);
      if (!dtUtc) {
        // Don't spam the console — parse failures are common for tentative
        // events. We just skip them.
        continue;
      }

      const country = node.querySelector('country')?.textContent?.trim() || '';
      const impact = node.querySelector('impact')?.textContent?.trim() || '';
      const title = node.querySelector('title')?.textContent?.trim() || '';
      const forecast = node.querySelector('forecast')?.textContent?.trim() || '';
      const previous = node.querySelector('previous')?.textContent?.trim() || '';
      const actual = node.querySelector('actual')?.textContent?.trim() || '';

      id++;
      events.push({
        id: `fe-${id}`,
        title,
        country,
        dateUtc: dtUtc.toISOString(),
        dateLocal: dtUtc.toLocaleDateString('en-CA'), // YYYY-MM-DD format
        timeLocal: dtUtc.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        impact: impact.toLowerCase(), // high, medium, low
        forecast,
        previous,
        actual,
        isFromApi: true
      });
    }
  } catch (error) {
    console.error('Error parsing economic calendar XML:', error);
  }

  return events;
};

/**
 * Parse JSON feed from FairEconomy into event objects.
 * JSON structure: array of { title, country, date, impact, forecast, previous }
 * The `date` field is a full ISO 8601 string with timezone offset (Eastern Time),
 * e.g. "2026-02-01T05:15:00-05:00". There is no separate `time` field.
 *
 * @param {string} jsonText - Raw JSON string from the API
 * @returns {Array} Array of parsed event objects
 */
const parseEventsJson = (jsonText) => {
  const events = [];
  if (!jsonText || typeof jsonText !== 'string') return events;

  try {
    const data = JSON.parse(jsonText);
    if (!Array.isArray(data)) {
      console.warn('Economic calendar JSON: expected array, got', typeof data);
      return events;
    }

    console.log(`Economic calendar: parsing ${data.length} JSON events`);
    let id = 0;

    for (const item of data) {
      const dateStr = item.date?.trim();
      if (!dateStr) continue;

      // Parse ISO 8601 date string (e.g. "2026-02-01T05:15:00-05:00")
      const dt = new Date(dateStr);
      if (isNaN(dt.getTime())) continue;

      const country = item.country?.trim() || '';
      const impact = item.impact?.trim() || '';
      const title = item.title?.trim() || '';
      const forecast = item.forecast?.trim() || '';
      const previous = item.previous?.trim() || '';
      const actual = item.actual?.trim() || '';

      id++;
      events.push({
        id: `fe-${id}`,
        title,
        country,
        dateUtc: dt.toISOString(),
        dateLocal: dt.toLocaleDateString('en-CA'),
        timeLocal: dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        impact: impact.toLowerCase(),
        forecast,
        previous,
        actual,
        isFromApi: true
      });
    }
  } catch (error) {
    console.error('Error parsing economic calendar JSON:', error);
  }

  return events;
};

/**
 * Parse CSV feed from FairEconomy into event objects.
 * CSV columns: title,country,date,time,impact,forecast,previous
 * Times are Eastern Time.
 *
 * @param {string} csvText - Raw CSV string from the API
 * @returns {Array} Array of parsed event objects
 */
const parseEventsCsv = (csvText) => {
  const events = [];
  if (!csvText || typeof csvText !== 'string') return events;

  try {
    const lines = csvText.split('\n').filter(l => l.trim());
    if (lines.length < 2) return events; // need header + at least 1 row

    // Parse header to find column indices
    const header = lines[0].split(',').map(h => h.trim().toLowerCase());
    const col = {
      title: header.indexOf('title'),
      country: header.indexOf('country'),
      date: header.indexOf('date'),
      time: header.indexOf('time'),
      impact: header.indexOf('impact'),
      forecast: header.indexOf('forecast'),
      previous: header.indexOf('previous'),
      actual: header.indexOf('actual')
    };

    // Need at least the date column (time may be embedded in date as ISO 8601)
    if (col.date === -1) {
      console.warn('Economic calendar CSV: missing date column');
      return events;
    }

    console.log(`Economic calendar: parsing ${lines.length - 1} CSV rows`);
    let id = 0;

    for (let i = 1; i < lines.length; i++) {
      // Simple CSV parse (handles quoted fields with commas)
      const fields = [];
      let current = '';
      let inQuotes = false;
      for (const ch of lines[i]) {
        if (ch === '"') {
          inQuotes = !inQuotes;
        } else if (ch === ',' && !inQuotes) {
          fields.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
      fields.push(current.trim());

      const timeText = col.time >= 0 ? fields[col.time] : '';
      const dateText = col.date >= 0 ? fields[col.date] : '';
      if (!dateText) continue;

      let dtUtc;
      try {
        // Try ISO 8601 first (e.g. "2026-02-01T05:15:00-05:00")
        // Then fall back to combined date+time
        const combined = timeText ? `${dateText} ${timeText}` : dateText;
        const parsed = new Date(combined);
        if (isNaN(parsed.getTime())) continue;
        dtUtc = parsed;
      } catch (e) {
        continue;
      }

      if (isNaN(dtUtc.getTime())) continue;

      const country = col.country >= 0 ? (fields[col.country] || '') : '';
      const impact = col.impact >= 0 ? (fields[col.impact] || '') : '';
      const title = col.title >= 0 ? (fields[col.title] || '') : '';
      const forecast = col.forecast >= 0 ? (fields[col.forecast] || '') : '';
      const previous = col.previous >= 0 ? (fields[col.previous] || '') : '';
      const actual = col.actual >= 0 ? (fields[col.actual] || '') : '';

      id++;
      events.push({
        id: `fe-${id}`,
        title,
        country,
        dateUtc: dtUtc.toISOString(),
        dateLocal: dtUtc.toLocaleDateString('en-CA'),
        timeLocal: dtUtc.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        impact: impact.toLowerCase(),
        forecast,
        previous,
        actual,
        isFromApi: true
      });
    }
  } catch (error) {
    console.error('Error parsing economic calendar CSV:', error);
  }

  return events;
};

/**
 * Get cached events from localStorage
 */
const getCachedEvents = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.error('Error reading economic calendar cache:', error);
  }
  return null;
};

/**
 * Save events to localStorage cache
 * IMPORTANT: Only caches if events were actually found,
 * to avoid caching empty results from rate-limited responses.
 */
const saveToCache = (events) => {
  if (!events || events.length === 0) {
    console.log('Economic calendar: skipping cache of empty result');
    return;
  }
  try {
    const cacheData = {
      timestamp: Date.now(),
      events: events
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    console.log(`Economic calendar: cached ${events.length} events`);
  } catch (error) {
    console.error('Error saving economic calendar cache:', error);
  }
};

/**
 * Check if cache is expired (48 hour window)
 */
const isCacheExpired = (timestamp) => {
  return Date.now() - timestamp > CACHE_DURATION;
};

/**
 * Fetch economic events from FairEconomy.media (JSON/CSV/XML cascade).
 * Features:
 *  - Multi-format cascade: JSON → CSV → XML (each has independent rate limit)
 *  - In-flight request deduplication (concurrent callers share one fetch)
 *  - Minimum interval between fetch attempts (3 min) to avoid 429
 *  - Cache validation (won't cache empty/rate-limited responses)
 *  - Stale cache fallback on errors
 *
 * @param {boolean} forceRefresh - Bypass cache (for manual refresh button)
 * @returns {Promise<Array>} Array of economic events
 */
export const fetchEconomicEvents = async (forceRefresh = false) => {
  // Check cache first (unless force refresh)
  const cached = getCachedEvents();
  if (!forceRefresh && cached && !isCacheExpired(cached.timestamp)) {
    console.log('Using cached economic calendar data');
    return cached.events;
  }

  // Deduplicate: if a fetch is already in flight, piggyback on it
  if (_inflightPromise) {
    console.log('Economic calendar: reusing in-flight request');
    return _inflightPromise;
  }

  // Rate limit guard — see MIN_FETCH_INTERVAL definition above (4hr)
  const now = Date.now();
  if (now - _lastFetchAttempt < MIN_FETCH_INTERVAL) {
    const waitSec = Math.ceil((MIN_FETCH_INTERVAL - (now - _lastFetchAttempt)) / 1000);
    console.log(`Economic calendar: rate limit guard, ${waitSec}s until next fetch allowed`);
    // Return stale cache if available, otherwise empty
    if (cached) return cached.events;
    return [];
  }

  _lastFetchAttempt = now;

  // Create the fetch promise (shared by any concurrent callers)
  _inflightPromise = (async () => {
    try {
      console.log('Fetching economic calendar from FairEconomy.media...');
      let rawData, format;

      // Check if running in Electron (use IPC with multi-format cascade)
      if (window.electronAPI && window.electronAPI.fetchEconomicCalendar) {
        console.log('Using Electron IPC for multi-format fetch (JSON → CSV → XML)');
        const result = await window.electronAPI.fetchEconomicCalendar({});

        if (!result.success) {
          throw new Error(result.error || 'All format fetches failed');
        }
        rawData = result.data;
        format = result.format || 'xml'; // fallback to xml parsing
      } else {
        // Direct fetch fallback (dev mode) - try JSON first
        console.log('Using direct fetch (dev mode)');
        try {
          const response = await fetch('https://nfs.faireconomy.media/ff_calendar_thisweek.json');
          if (response.ok) {
            rawData = await response.text();
            format = 'json';
          }
        } catch (e) { /* fall through */ }

        if (!rawData) {
          const response = await fetch('http://nfs.faireconomy.media/ff_calendar_thisweek.xml');
          if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          rawData = await response.text();
          format = 'xml';
        }
      }

      // Parse based on format
      let events;
      if (format === 'json') {
        events = parseEventsJson(rawData);
      } else if (format === 'csv') {
        events = parseEventsCsv(rawData);
      } else {
        events = parseEventsXml(rawData);
      }
      console.log(`Parsed ${events.length} economic events from ${format.toUpperCase()}`);

      // Only cache if we got real events (avoids caching rate-limit HTML)
      if (events.length > 0) {
        saveToCache(events);
      }

      return events;
    } catch (error) {
      console.error('Error fetching economic calendar:', error);

      // Return stale cache if available
      if (cached) {
        console.log('Fetch failed, using stale cache');
        return cached.events;
      }

      throw error;
    } finally {
      _inflightPromise = null;
    }
  })();

  return _inflightPromise;
};

/**
 * Filter events by date (YYYY-MM-DD)
 */
export const filterEventsByDate = (events, date) => {
  if (!events || !Array.isArray(events)) return [];
  return events.filter(event => event.dateLocal === date);
};

/**
 * Filter events by settings (currency and impact)
 */
export const filterEventsBySettings = (events, settings) => {
  if (!events || !Array.isArray(events)) return [];
  if (!settings) return events;

  const { currencies = ['USD'], impactLevels = ['high', 'medium', 'low'] } = settings;

  return events.filter(event => {
    const currencyMatch = currencies.length === 0 || currencies.includes(event.country);
    const impactMatch = impactLevels.length === 0 || impactLevels.includes(event.impact);
    return currencyMatch && impactMatch;
  });
};

/**
 * Get events for a specific date with filters applied
 * @param {string} date - YYYY-MM-DD
 * @param {Object} settings - { currencies, impactLevels }
 * @param {boolean} forceRefresh - bypass cache
 */
export const getEventsForDate = async (date, settings, forceRefresh = false) => {
  const allEvents = await fetchEconomicEvents(forceRefresh);
  const dateFiltered = filterEventsByDate(allEvents, date);
  const settingsFiltered = filterEventsBySettings(dateFiltered, settings);
  return settingsFiltered;
};

/**
 * Transform a parsed FairEconomy event to the display format
 * used by EconomicEventsPanel.
 */
export const normalizeEvent = (apiEvent) => {
  return {
    id: apiEvent.id,
    time: apiEvent.timeLocal,
    name: apiEvent.title || 'Unknown Event',
    importance: apiEvent.impact || 'medium',
    currency: apiEvent.country || 'USD',
    actual: apiEvent.actual || '',
    forecast: apiEvent.forecast || '',
    previous: apiEvent.previous || '',
    isFromApi: true
  };
};

/**
 * Clear the cache (useful for forcing a refresh)
 */
export const clearCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
    console.log('Economic calendar cache cleared');
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Get cache status for UI display
 */
export const getCacheStatus = () => {
  const cached = getCachedEvents();
  if (!cached) {
    return { hasCachedData: false, isExpired: true };
  }

  const isExpired = isCacheExpired(cached.timestamp);
  const age = Date.now() - cached.timestamp;
  const minutesOld = Math.floor(age / (1000 * 60));

  return {
    hasCachedData: true,
    isExpired,
    eventCount: cached.events?.length || 0,
    minutesOld,
    timestamp: new Date(cached.timestamp).toLocaleString()
  };
};

// Keep setApiKey as no-op for backward compatibility
export const setApiKey = () => {};

const economicCalendarService = {
  fetchEconomicEvents,
  filterEventsByDate,
  filterEventsBySettings,
  getEventsForDate,
  normalizeEvent,
  clearCache,
  getCacheStatus
};

export default economicCalendarService;

